import { useMemo, useState } from "react";
import { useCastle } from "../api/castle";
import { useMe, useUnitCatalog } from "../api/army";
import { useHeroes } from "../api/heroes";
import {
  useAttack,
  useMarches,
  useOpponents,
  useScout,
} from "../api/marches";
import { Loading } from "./Loading";
import { ErrorView } from "./ErrorView";
import { ResourceBar } from "../components/ResourceBar";
import { ScreenBanner } from "../components/ScreenBanner";
import { MarchesList } from "../components/MarchesList";
import { OpponentList } from "../components/OpponentList";
import { ScoutView } from "../components/ScoutView";
import { AttackComposer } from "../components/AttackComposer";
import { BattleHistory } from "../components/BattleHistory";
import { PveCampList } from "../components/PveCampList";
import { PveComposer } from "../components/PveComposer";
import { usePveAttack, usePveCamps } from "../api/pve";
import { useNow } from "../lib/ticker";
import { OpponentEntry, ScoutReport } from "../types/marches";
import { PveCampEntry } from "../types/pve";

type View = "list" | "opponents" | "scout" | "compose" | "history" | "pve" | "pve-compose";

const TRAVEL_BASE_SECONDS = 30;

export function MarchesScreen() {
  const castle = useCastle();
  const me = useMe();
  const units = useUnitCatalog();
  const heroes = useHeroes();
  const marches = useMarches();
  const opponents = useOpponents(true);
  const scout = useScout();
  const attack = useAttack();
  const now = useNow(1000);

  const [view, setView] = useState<View>("list");
  const [selectedOpponent, setSelectedOpponent] = useState<OpponentEntry | null>(null);
  const [scoutReport, setScoutReport] = useState<ScoutReport | null>(null);
  const [selectedCamp, setSelectedCamp] = useState<PveCampEntry | null>(null);
  const pveCamps = usePveCamps(view === "pve" || view === "pve-compose");
  const pveAttack = usePveAttack();

  const unitsByIdMap = useMemo(() => {
    const m = new Map<string, NonNullable<typeof units.data>[number]>();
    units.data?.forEach((u) => m.set(u.id, u));
    return m;
  }, [units.data]);

  if (castle.isPending || me.isPending || units.isPending || marches.isPending) {
    return <Loading message="Маршруты строятся..." />;
  }
  if (castle.isError || me.isError || units.isError || marches.isError) {
    return <ErrorView message="Не получилось загрузить марши." />;
  }

  const data = castle.data!;
  const garrison = data.state.garrison;
  const marchCapacity = data.marchCapacity;
  const marchSpeedReductionPct = data.researchBonuses.marchSpeed ?? 0;
  const manaCurrent = data.state.resources.mana;

  const errorScout = pickError(scout.error);
  const errorAttack = pickError(attack.error);

  return (
    <div className="flex h-full flex-col">
      <ScreenBanner kind="marches" title="Походы войск" />
      <ResourceBar resources={data.state.resources} safeStorage={data.safeStorage} />

      <div className="flex-1 overflow-hidden">
        {view === "list" && (
          <MarchesList
            marches={marches.data ?? []}
            now={now}
            unitsById={unitsByIdMap}
            onFindOpponent={() => setView("opponents")}
            onOpenPve={() => setView("pve")}
            onOpenHistory={() => setView("history")}
          />
        )}

        {view === "pve" && (
          <PveCampList
            camps={pveCamps.data ?? []}
            now={now}
            onPick={(c) => {
              setSelectedCamp(c);
              pveAttack.reset();
              setView("pve-compose");
            }}
            onBack={() => setView("list")}
          />
        )}

        {view === "pve-compose" && selectedCamp && (
          <PveComposer
            camp={selectedCamp}
            garrison={garrison}
            unitsById={unitsByIdMap}
            ownedHeroes={(heroes.data ?? []).filter((h) => h.owned)}
            marchCapacity={marchCapacity}
            marchSpeedReductionPct={marchSpeedReductionPct}
            travelBaseSeconds={TRAVEL_BASE_SECONDS}
            onBack={() => setView("pve")}
            onConfirm={(units, heroIds, stratagem) =>
              pveAttack.mutate(
                { campId: selectedCamp.id, units, heroIds, stratagem },
                {
                  onSuccess: () => {
                    setSelectedCamp(null);
                    setView("list");
                  },
                },
              )
            }
            pending={pveAttack.isPending}
            error={pickError(pveAttack.error)}
          />
        )}

        {view === "opponents" && (
          <OpponentList
            opponents={opponents.data ?? []}
            onPick={(op) => {
              setSelectedOpponent(op);
              setScoutReport(null);
              scout.reset();
              setView("scout");
            }}
            onBack={() => setView("list")}
          />
        )}

        {view === "scout" && selectedOpponent && (
          <ScoutView
            opponent={selectedOpponent}
            report={scoutReport}
            manaCurrent={manaCurrent}
            scoutCost={50}
            loading={scout.isPending}
            error={errorScout}
            onScout={() => {
              scout.mutate(selectedOpponent.id, {
                onSuccess: (data) => {
                  setScoutReport(data);
                },
              });
            }}
            onAttack={() => setView("compose")}
            onBack={() => setView("opponents")}
          />
        )}

        {view === "compose" && selectedOpponent && (
          <AttackComposer
            opponent={selectedOpponent}
            garrison={garrison}
            unitsById={unitsByIdMap}
            ownedHeroes={(heroes.data ?? []).filter((h) => h.owned)}
            marchCapacity={marchCapacity}
            marchSpeedReductionPct={marchSpeedReductionPct}
            travelBaseSeconds={TRAVEL_BASE_SECONDS}
            onBack={() => setView("scout")}
            onConfirm={(units, heroIds, stratagem) => {
              attack.mutate(
                { defenderId: selectedOpponent.id, units, heroIds, stratagem },
                {
                  onSuccess: () => {
                    setScoutReport(null);
                    setSelectedOpponent(null);
                    setView("list");
                  },
                },
              );
            }}
            pending={attack.isPending}
            error={errorAttack}
          />
        )}

        {view === "history" && (
          <BattleHistory
            enabled={true}
            selfId={me.data?.id ?? null}
            onBack={() => setView("list")}
          />
        )}
      </div>
    </div>
  );
}

function pickError(err: unknown): string | null {
  if (!err || typeof err !== "object") return null;
  const resp = (err as { response?: { data?: { error?: string } } }).response;
  return resp?.data?.error ?? null;
}
